import { useEffect, useRef, useState, type PointerEvent } from 'react';
import {
  ANNOTATION_COLOURS,
  ANNOTATION_MAX_POINTS,
  type AnnotationColour,
  type PhotoAnnotation,
  type PhotoAnnotationMark,
} from '@qms360/shared';
import { AnnotationOverlay } from './AnnotationOverlay.js';
import { StoredImage } from './StoredImage.js';
import { t } from '../../lib/i18n.js';

/**
 * Drawing on a photo (pass 8; owner's decision: pen, arrow and text, with
 * undo). The marks are saved beside the photo; the photo is never altered.
 */

type Tool = 'pen' | 'arrow' | 'text';

const TOOLS: { key: Tool; label: string }[] = [
  { key: 'pen', label: 'Pen' },
  { key: 'arrow', label: 'Arrow' },
  { key: 'text', label: 'Text' },
];

const COLOUR_LABELS: Record<AnnotationColour, string> = { red: 'Red', yellow: 'Yellow', white: 'White', black: 'Black' };

/** Text is placed at this fraction of the photo's width. */
const TEXT_SIZE = 0.05;

function pointCount(marks: PhotoAnnotationMark[]): number {
  return marks.reduce((total, mark) => total + (mark.kind === 'pen' ? mark.points.length : 2), 0);
}

export function PhotoAnnotator({
  fileId,
  alt,
  initial,
  busy,
  error,
  onSave,
  onCancel,
}: {
  fileId: string;
  alt: string;
  initial: PhotoAnnotation | null;
  busy: boolean;
  error: string | null;
  onSave: (annotation: PhotoAnnotation | null) => void;
  onCancel: () => void;
}) {
  const [marks, setMarks] = useState<PhotoAnnotationMark[]>(initial?.marks ?? []);
  const [drawing, setDrawing] = useState<PhotoAnnotationMark | null>(null);
  const [tool, setTool] = useState<Tool>('pen');
  const [colour, setColour] = useState<AnnotationColour>('red');
  const [text, setText] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const layer = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const position = (event: PointerEvent<SVGSVGElement>): [number, number] => {
    const box = layer.current!.getBoundingClientRect();
    const clamp = (value: number) => Math.min(1, Math.max(0, value));
    return [clamp((event.clientX - box.left) / (box.width || 1)), clamp((event.clientY - box.top) / (box.height || 1))];
  };

  const down = (event: PointerEvent<SVGSVGElement>) => {
    const at = position(event);
    if (tool === 'text') {
      if (!text.trim()) {
        setNotice(t("Type the text first, then tap where it goes."));
        return;
      }
      setMarks([...marks, { kind: 'text', colour, at, text: text.trim(), size: TEXT_SIZE }]);
      setText('');
      setNotice(null);
      return;
    }
    if (pointCount(marks) >= ANNOTATION_MAX_POINTS) {
      setNotice(t("This photo has as many marks as it can keep. Undo some to draw more."));
      return;
    }
    layer.current?.setPointerCapture?.(event.pointerId);
    setDrawing(tool === 'pen' ? { kind: 'pen', colour, points: [at, at] } : { kind: 'arrow', colour, from: at, to: at });
  };

  const move = (event: PointerEvent<SVGSVGElement>) => {
    if (!drawing) return;
    const at = position(event);
    if (drawing.kind === 'pen') {
      const last = drawing.points[drawing.points.length - 1]!;
      // Skip points closer than a few pixels: they add bytes, not detail.
      if (Math.hypot(at[0] - last[0], at[1] - last[1]) < 0.004) return;
      if (pointCount(marks) + drawing.points.length >= ANNOTATION_MAX_POINTS) return;
      setDrawing({ ...drawing, points: [...drawing.points, at] });
    } else if (drawing.kind === 'arrow') {
      setDrawing({ ...drawing, to: at });
    }
  };

  const up = () => {
    if (!drawing) return;
    const keep =
      drawing.kind === 'pen'
        ? drawing.points.length >= 2
        : drawing.kind === 'arrow' && Math.hypot(drawing.to[0] - drawing.from[0], drawing.to[1] - drawing.from[1]) > 0.01;
    if (keep) setMarks([...marks, drawing]);
    setDrawing(null);
  };

  const shown: PhotoAnnotation = { version: 1, marks: drawing ? [...marks, drawing] : marks };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="annotate-heading"
        className="flex max-h-full w-full max-w-4xl flex-col gap-3 overflow-auto rounded-md bg-surface p-4"
      >
        <h2 id="annotate-heading" className="m-0 text-base font-semibold">
          {t("Draw on the photo")}
        </h2>
        <p className="m-0 text-sm text-content-secondary">
          {t("The photo itself is kept as it was taken; your marks are saved beside it.")}
        </p>

        <div className="flex flex-wrap items-end gap-3 text-sm">
          <fieldset className="m-0 flex gap-1 border-0 p-0">
            <legend className="sr-only">{t("Tool")}</legend>
            {TOOLS.map((each) => (
              <button
                key={each.key}
                type="button"
                aria-pressed={tool === each.key}
                className={`rounded-sm border px-3 py-1.5 ${
                  tool === each.key ? 'border-ag-purple bg-ag-purple text-content-inverse' : 'border-border-strong'
                }`}
                onClick={() => setTool(each.key)}
              >
                {t(each.label)}
              </button>
            ))}
          </fieldset>
          <label className="flex flex-col">
            <span className="mb-1 text-content-secondary">{t("Colour")}</span>
            <select
              className="rounded-sm border border-border-strong px-2 py-1.5"
              value={colour}
              onChange={(event) => setColour(event.target.value as AnnotationColour)}
            >
              {(Object.keys(ANNOTATION_COLOURS) as AnnotationColour[]).map((key) => (
                <option key={key} value={key}>
                  {t(COLOUR_LABELS[key])}
                </option>
              ))}
            </select>
          </label>
          {tool === 'text' && (
            <label className="flex flex-col">
              <span className="mb-1 text-content-secondary">{t("Text, then tap the photo")}</span>
              <input
                className="rounded-sm border border-border-strong px-2 py-1.5"
                maxLength={200}
                value={text}
                onChange={(event) => setText(event.target.value)}
              />
            </label>
          )}
          <button
            type="button"
            className="rounded-sm border border-border-strong px-3 py-1.5 disabled:opacity-50"
            disabled={marks.length === 0}
            onClick={() => setMarks(marks.slice(0, -1))}
          >
            {t("Undo")}
          </button>
          <button
            type="button"
            className="rounded-sm border border-border-strong px-3 py-1.5 disabled:opacity-50"
            disabled={marks.length === 0}
            onClick={() => setMarks([])}
          >
            {t("Clear all")}
          </button>
        </div>

        <div className="relative inline-block self-center [container-type:inline-size]">
          <StoredImage fileId={fileId} alt={alt} className="block max-h-[65vh] max-w-full select-none" />
          <AnnotationOverlay annotation={shown} />
          <svg
            ref={layer}
            data-testid="annotation-layer"
            aria-hidden="true"
            className="absolute inset-0 h-full w-full cursor-crosshair touch-none"
            onPointerDown={down}
            onPointerMove={move}
            onPointerUp={up}
            onPointerCancel={up}
          />
        </div>

        {(notice || error) && (
          <p role="alert" className="m-0 text-sm text-tone-critical">
            {error ?? notice}
          </p>
        )}
        <p className="m-0 text-sm text-content-secondary" aria-live="polite">
          {marks.length === 0 ? t("No marks yet.") : t("{0} mark{1}.", { 0: marks.length, 1: marks.length === 1 ? '' : 's' })}
        </p>

        <div className="flex gap-2">
          <button
            type="button"
            className="rounded-sm bg-ag-purple px-4 py-2 text-sm font-medium text-content-inverse disabled:opacity-60"
            disabled={busy}
            onClick={() => onSave(marks.length ? { version: 1, marks } : null)}
          >
            {busy ? t("Saving…") : t("Save the marks")}
          </button>
          <button type="button" className="rounded-sm border border-border-strong px-4 py-2 text-sm" onClick={onCancel}>
            {t("Cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}
